<template>
  <v-container>
    <v-card class="text-secondary" title="Discover detailed information about the planets of the galaxy. Use the search to explore!" flat>
      <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" variant="outlined" hide-details single-line dense></v-text-field>

      <v-data-table :items="filteredPlanets" :headers="headers" :search="search" :items-per-page="25">
        <template v-slot:loading>
          <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
        </template>

        <!-- Table Rows -->
        <template v-slot:item="props">
          <tr>
            <td>
              <!-- Use a method to get the planet icon -->
              <img :src="getPlanetIcon(props.item.name)" :alt="`${props.item.name} Icon`" class="planet-icon" />
            </td>
            <td>
              <a :href="props.item.uri" target="_blank" class="text-decoration-none">
                {{ props.item.name }}
              </a>
            </td>
            <td>{{ props.item.location }}</td>
            <td>{{ Number(props.item.population).toLocaleString() }}</td>
            <td>{{ Number(props.item.diameter).toLocaleString() + ' ' + props.item.diameter_unit }}</td>
            <td>{{ props.item.orbital_period + ' ' + props.item.orbital_period_unit }}</td>
            <td>{{ props.item.rotation_period + ' ' + props.item.rotation_period_unit }}</td>
            <td>
              <div style="max-height: 100px; overflow-y: auto;" class="my-2">
                <div v-for="(region_URI, index) in (props.item.regions_URI ? props.item.regions_URI.split(';') : [])" :key="index">
                  <a v-if="region_URI.trim() !== ''" :href="region_URI.trim()" target="_blank" class="text-decoration-none">
                    {{ (props.item.regions ? props.item.regions.split(';')[index].trim() : 'Unknown') }}
                  </a>
                  <span v-else>Unknown</span>
                </div>
              </div>
            </td>
            <td>
              <div style="max-height: 100px; overflow-y: auto;" class="my-2">
                <div v-for="(work_URI, index) in (props.item.works_URI ? props.item.works_URI.split(';') : [])" :key="index">
                  <a v-if="work_URI.trim() !== ''" :href="work_URI.trim()" target="_blank" class="text-decoration-none">
                    {{
                      (props.item.works && props.item.works.split(';')[index]?.trim()) || 'Unknown'
                    }}
                  </a>
                  <span v-else>Unknown</span>
                </div>
              </div>
            </td>
            <td>
              <a :href="props.item.wookiepedia_url" target="_blank" class="text-decoration-none">
                Go to {{ props.item.wookiepedia_url.replace('https://starwars.fandom.com/wiki/', '') }} page
              </a>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import apiClient from '@/apiClient';
import { useHead } from '@unhead/vue';

export default {
  name: "Planets_Search",
  data() {
    return {
      search: '',
      planets: [],
      headers: [
        { title: "", value: "icon",align: "center"},
        { title: "Name", value: "name",align: "center",sortable: true},
        { title: "Location", value: "location",align: "center"},
        { title: "Population", value: "population",align: "center"},
        { title: "Diameter", value: "diameter",align: "center"},
        { title: "Orbital Period", value: "orbital_period",align: "center"},
        { title: "Rotation Period", value: "rotation_period",align: "center"},
        { title: "Regions", value: "regions",align: "center"},
        { title: "Works", value: "works",align: "center"},
        { title: "Wookieepedia Page", value: "wookiepedia_url",align: "center"}
      ]
    };
  },
  computed: {
    filteredPlanets() {
      const query = this.search.toLowerCase();
      return this.planets.filter(planet => (planet.name && planet.name.toLowerCase().includes(query)) || (planet.works && planet.works.toLowerCase().includes(query)) || (planet.location && planet.location.toLowerCase().includes(query))
      );
    }
  },
  methods: {
    getPlanetIcon(planetName) {
      try {
        // Construct the path to the planet's GIF file
        return require(`@/assets/planets_icons/${planetName.toLowerCase().replace(/\s+/g, '_')}.gif`);
      } catch (error) {
        // Fallback to a default icon if the planet's GIF is not found
        return require('@/assets/planets_icons/default.gif');
      }
    }
  },
  mounted() {
    // Fetch data from backend
    apiClient.get("/api/planets")
      .then(response => {
        this.planets = response.data;
        console.log("Planets data loaded:", this.planets);
      })
      .catch(error => {
        console.error("Error fetching planets:", error);
      });
  },
  setup() {
    useHead({
      title: "Star Wars Planets – Explore by Location, Population & More",
      meta: [
        {
          name: "description",
          content: "Discover detailed data on Star Wars planets. Search by location, population, diameter, climate, and other attributes in the Holonet Galactic Encyclopedia. Explore the galaxy’s diverse worlds, powered by Wikidata.",
        },
        {
          property: "og:title",
          content: "Star Wars Planets – Explore by Location, Population & More",
        },
        {
          property: "og:description",
          content: "Search and explore Star Wars planets with detailed filters: location, population, diameter, climate, and more. Dive into the galaxy’s worlds in this interactive encyclopedia.",
        },
        {
          property: "og:url",
          content: "https://sw-holonet.com/planets/table",
        },
        { property: "og:type", content: "website" },
        {
          name: "twitter:title",
          content: "Star Wars Planets – Explore by Location, Population & More",
        },
        {
          name: "twitter:description",
          content: "Explore Star Wars planets with detailed data on location, population, and more. Discover the galaxy’s worlds in the Holonet Galactic Encyclopedia.",
        },
        { name: "twitter:card", content: "summary" },
        {
          name: "keywords",
          content: "Star Wars planets, Star Wars galaxy, planet database, Star Wars locations, planet population, planet diameter, planet climate, Wikidata Star Wars, Holonet planets",
        },
      ],
    });
  }
};
</script>

<style>
.planet-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.v-col {
  display: flex;
  align-items: center;
}

a {
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}
</style>
